import React, { useEffect, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { createUseStyles } from 'react-jss';
import { S, A, DARK } from './tokens';

type Props = {
  title: React.ReactNode;
  children: React.ReactNode;
  /** Buttons at the bottom right. */
  footer?: React.ReactNode;
  onClose: () => void;
  width?: number;
  testId?: string;
};

const useStyles = createUseStyles({
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 3000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    background: 'rgba(23, 26, 32, 0.36)',
    animation: '$gxFade 120ms ease-out',
    [DARK]: { background: S.scrim }
  },
  '@keyframes gxFade': { from: { opacity: 0 }, to: { opacity: 1 } },
  dialog: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxHeight: 'calc(100vh - 32px)',
    background: A.surface,
    borderRadius: 12,
    boxShadow: A.pop,
    fontFamily: S.ui,
    color: S.fg,
    outline: 'none'
  },
  head: { display: 'flex', alignItems: 'center', gap: 12, padding: '18px 16px 10px 24px' },
  title: { flex: 1, margin: 0, fontSize: 18, fontWeight: 600, lineHeight: 1.3 },
  close: {
    all: 'unset',
    width: 30,
    height: 30,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    color: S.muted,
    cursor: 'pointer',
    '& .material-icons': { fontSize: 20 },
    '&:hover': { background: S.chromeHover, color: S.fg },
    '&:focus-visible': A.focus
  },
  body: { padding: '4px 24px 20px', overflowY: 'auto', fontSize: 14, lineHeight: 1.5 },
  foot: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 24px 18px', borderTop: `1px solid ${S.line}` }
});

/** A window over the screen for a task or a list: title, content, close. Escape closes it. */
const Dialog: React.FC<Props> = ({ title, children, footer, onClose, width = 520, testId }) => {
  const c = useStyles();
  const ref = useRef<HTMLDivElement>(null);
  const returnTo = useRef<Element | null>(typeof document !== 'undefined' ? document.activeElement : null);
  const titleId = useRef(`gx-dialog-${Math.random().toString(36).slice(2)}`).current;

  useEffect(() => {
    // Into the dialog, unless something inside already took the focus (an autofocused field).
    if (ref.current && !ref.current.contains(document.activeElement)) ref.current.focus();
  }, []);

  // Back to where focus was, in the same step that removes the dialog.
  useLayoutEffect(() => {
    const back = returnTo.current as HTMLElement | null;
    return () => { if (back && typeof back.focus === 'function' && document.contains(back)) back.focus(); };
  }, []);

  return ReactDOM.createPortal(
    <div className={c.backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} onClick={e => e.stopPropagation()}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={c.dialog}
        style={{ maxWidth: width }}
        data-testid={testId}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); } }}
      >
        <div className={c.head}>
          <h2 id={titleId} className={c.title}>{title}</h2>
          <button type="button" className={c.close} onClick={onClose} aria-label="Close">
            <i className="material-icons" aria-hidden>close</i>
          </button>
        </div>
        <div className={c.body}>{children}</div>
        {footer && <div className={c.foot}>{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default Dialog;
